import React, { useMemo } from 'react';
import styled, { DefaultTheme, useTheme } from 'styled-components/macro';

import {
  FOLDER_ACCESS_RIGHTS,
  FOLDER_TYPES,
  FoldersV2,
  useGetFoldersV2,
} from 'lib/api/folders/getFoldersV2';
import { ALL_FOLDERS } from 'lib/const/Folders';
import { useAuth } from 'lib/context';
import Select, {
  ControlProps,
  CSSObjectWithLabel,
  OptionProps,
} from 'react-select';
import { getControlStyles, getOptionStyles } from '../Dropdown';
import { DropdownIndicator } from './dropdownIndicator';
import { CustomOption, FolderOption } from './customOption';
import { CustomMenuList } from './customMenuList';
import { GroupHeading } from './groupHeading';
import { Flex } from 'lib/components/styles/layout';
import { CEModalFolders } from 'lib/components/modal/foldersModals/CE_Modal';
import { mapFolderToOption } from 'lib/utils/folders';

const Label = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.secondary[60]};
  margin-bottom: 8px;
`;

export enum ACCESS_FILTER {
  VIEW = 'VIEW',
  EDIT = 'EDIT',
}

type Props = {
  value?: FolderOption;
  options?: Array<FolderOption>;
  position?: 'bottom' | 'left';
  onChange: (option: FolderOption, value?: FoldersV2) => void;
  showAllFolders?: boolean;
  maxMenuHeight?: number;
  showCreateButton?: boolean;
  openMenuOnFocus?: boolean;
  placeholder?: React.ReactNode;
  hideControls?: boolean;
  containerWidth?: string;
  menuWidth?: string;
  optionWidth?: string;
  margin?: string;
  label?: string;
  showEditAndUploadFolders?: boolean;
  accessFilter?: ACCESS_FILTER;
  dropdownHeight?: string | number;
  getShared?: boolean;
  setFolders?: (folders: FoldersV2[]) => void;
  folderToDelete?: number;
  fetchFolders?: boolean;
  menuPortalTarget?: HTMLElement | null;
};

const menuStyles = (
  position: 'bottom' | 'left',
  color: string,
  width: string
) => {
  if (position === 'left') {
    return {
      width,
      color,
      top: '-50px',
      right: '185px',
      marginTop: 0,
      marginLeft: 0,
    };
  }
  return {
    width,
    color,
  };
};

const controlStyles = (
  hideControls: boolean,
  themes: DefaultTheme,
  props: ControlProps<FolderOption, false>
) => {
  if (hideControls) {
    return {
      border: 'none',
      boxShadow: 'none',
      '&:hover': {
        borderColor: themes.colors.secondary[100],
      },
    };
  }
  return {
    boxShadow: 'none',
    borderRadius: 6,
    minHeight: '40px',
    cursor: 'pointer',
    ...getControlStyles(props, themes),
    //@ts-ignore
    '&:hover': {
      borderColor: themes.colors.secondary[100],
    },
  };
};

const valueContainerStyles = (hideControls: boolean) => {
  if (hideControls) {
    return {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      flexWrap: 'nowrap',
      padding: '0px',
      height: '40px',
    };
  }
  return {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    flexWrap: 'nowrap',
  };
};

export const FolderV2Dropdown = (props: Props) => {
  const {
    value,
    onChange,
    position = 'bottom',
    showAllFolders = true,
    maxMenuHeight = 514,
    showCreateButton = true,
    openMenuOnFocus = false,
    placeholder,
    hideControls = false,
    containerWidth = '200px',
    menuWidth = '334px',
    optionWidth = '294px',
    margin = '0px',
    label,
    accessFilter = ACCESS_FILTER.VIEW,
    dropdownHeight = 'auto',
    getShared = true,
    setFolders,
    folderToDelete,
    fetchFolders = false,
    menuPortalTarget = document.body,
  } = props;
  const themes = useTheme();
  const { userData } = useAuth();
  const [showCreateFolderModal, setShowCreateFolderModal] =
    React.useState(false);

  const selectRef = React.useRef<any>(null);

  const { data: foldersResponse, isFetched } = useGetFoldersV2({
    params: { getShared },
    options: {
      refetchOnMount: fetchFolders ? 'always' : false,
    },
  });
  const groupedFolders = useMemo(
    () => foldersResponse?.folders || {},
    [foldersResponse?.folders]
  );
  const allFolderOption = {
    label: '',
    options: [{ label: ALL_FOLDERS.label, value: ALL_FOLDERS.value }],
  };

  const groupedOptions = Object.entries(groupedFolders).map(
    ([key, folders]) => ({
      label: key?.replaceAll('_', ' ') || '',
      options: (folders || [])
        .filter(
          folder =>
            accessFilter === ACCESS_FILTER.VIEW ||
            folder.userId?.toString() === userData.id ||
            folder.access === FOLDER_ACCESS_RIGHTS.EDIT
        )
        .map(folder => mapFolderToOption(folder, userData.id)),
    })
  );

  if (showAllFolders) {
    groupedOptions.unshift(allFolderOption);
  }
  const styles = {
    container: (base: CSSObjectWithLabel) => {
      return {
        ...base,
        width: containerWidth,
      };
    },

    control: (
      base: CSSObjectWithLabel,
      props: ControlProps<FolderOption, false>
    ): CSSObjectWithLabel => {
      return {
        ...base,
        ...controlStyles(hideControls, themes, props),
      };
    },
    singleValue: (base: CSSObjectWithLabel) => ({
      ...base,
      width: '95%',
    }),
    valueContainer: (base: CSSObjectWithLabel) =>
      ({
        ...base,
        ...valueContainerStyles(hideControls),
      }) as CSSObjectWithLabel,
    menuPortal: (base: CSSObjectWithLabel) => ({
      ...base,
      zIndex: 9999,
    }),
    menuList: (base: CSSObjectWithLabel) => ({
      ...base,
      padding: 0,
      margin: 0,
      height: dropdownHeight,
    }),
    menu: (base: CSSObjectWithLabel) => ({
      ...base,
      ...menuStyles(position, themes.colors.secondary[100], menuWidth),
    }),
    option: (
      base: CSSObjectWithLabel,
      props: OptionProps<FolderOption, false>
    ): CSSObjectWithLabel => {
      return {
        ...base,
        height: 44,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        transition: 'all 0.3s',
        zIndex: 1,
        margin: '0',
        width: optionWidth,
        //@ts-ignore
        ...getOptionStyles(props, themes),
      };
    },
    group: (base: CSSObjectWithLabel) => ({
      ...base,
      padding: 0,
    }),
  };

  const handleMouseEnter = () => {
    if (openMenuOnFocus) {
      selectRef.current?.setState({ menuIsOpen: true });
    }
  };

  const handleMouseLeave = () => {
    if (openMenuOnFocus) {
      selectRef.current?.setState({ menuIsOpen: false });
    }
  };

  const handleOnChange = (option: FolderOption) => {
    if (
      !(
        typeof option.value === 'string' &&
        /^[a-zA-Z0-9-_]+$/.test(option.value)
      )
    ) {
      return;
    }
    const folder =
      option.value !== ALL_FOLDERS.value
        ? Object.values(groupedFolders)
            .flat()
            .find(
              folder => folder.folderId.toString() === option.value?.toString()
            )
        : {
            folderId: ALL_FOLDERS.value,
            name: ALL_FOLDERS.label,
            isDeletable: false,
            isFolderShared: false,
            folderType: FOLDER_TYPES.PERSONAL,
          };
    onChange(option, folder);
  };

  const selectedOptions = groupedOptions
    .flatMap(group => group.options)
    .find(opt => opt.value.toString() === value?.value?.toString());

  React.useEffect(() => {
    if (isFetched && setFolders && !!groupedFolders) {
      setFolders(
        Object.values(groupedFolders)
          .flat()
          .filter(folder => folder.folderId !== ALL_FOLDERS.value)
      );
    }
  }, [isFetched, groupedFolders, setFolders]);

  const updatedFolders = groupedOptions.map(group => ({
    ...group,
    options: group.options.filter(
      option => Number(option.value) !== folderToDelete
    ),
  }));

  return isFetched ? (
    <Flex
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      margin={margin}
      width='auto'
    >
      {!!label?.length && <Label>{label}</Label>}
      <Select
        value={selectedOptions}
        ref={selectRef}
        options={folderToDelete ? updatedFolders : groupedOptions}
        placeholder={placeholder || 'Select a folder'}
        isDisabled={false}
        aria-label='Aria label is missing'
        menuPosition='absolute'
        menuPlacement='auto'
        maxMenuHeight={maxMenuHeight}
        menuShouldScrollIntoView={false}
        menuPortalTarget={menuPortalTarget}
        openMenuOnFocus={false}
        isLoading={false}
        components={{
          Option: CustomOption,
          DropdownIndicator: (indicatorProps: any) =>
            hideControls ? null : DropdownIndicator(indicatorProps),
          MenuList: (menuProps: any) =>
            CustomMenuList({
              menuProps,
              onCreateNewFolder: () => setShowCreateFolderModal(true),
              showCreateButton,
            }),
          GroupHeading: GroupHeading,
          IndicatorSeparator: () => null,
        }}
        styles={styles as any}
        isSearchable={true}
        onChange={option => handleOnChange(option as FolderOption)}
      />

      {showCreateFolderModal && (
        <CEModalFolders
          handleModalClose={(folder?: FoldersV2) => {
            if (folder) {
              onChange(
                {
                  label: folder.name,
                  value: folder.folderId.toString(),
                  isSharedFolder:
                    folder.userId?.toString() === userData.id &&
                    folder.folderType === FOLDER_TYPES.SHARED,
                  sharedBy: folder.sharedBy,
                },
                folder
              );
            }
            setShowCreateFolderModal(false);
          }}
        />
      )}
    </Flex>
  ) : (
    <></>
  );
};
